import type { NoteSummary } from '@/app/db/repo';
import {
  Archive,
  ArchiveRestore,
  Calculator,
  CalendarClock,
  ChefHat,
  ChevronDown,
  ListChecks,
  MoveHorizontal,
  Pin,
  PinOff,
  RotateCcw,
  Search,
  Square,
  SquareCheckBig,
  Table,
  Trash,
} from 'lucide-react';
import {
  type MouseEvent,
  useRef,
  useState,
} from 'react';
import { isMobile } from './mobile';
import { PopupMenu } from './PopupMenu';

const WIDTHS = [40, 50, 60, 70, 80, 90, 100];

type Props = {
  note: NoteSummary,
  onTogglePin: () => void,
  onToggleArchived: () => void,
  onWidth: (width: number) => void,
  // to the trash, and back out of it
  onTrash: () => void,
  onRestore: () => void,
  // for good, from the trash
  onDelete: () => void,
  // the open note's editor actions, offered here on a phone, where the
  // editor's own menu is the system's
  editor?: NoteMenuEditor,
};

export type NoteMenuEditor = {
  insertTable: () => void,
  insertRecipe: () => void,
  insertCalc: () => void,
  insertChecklist: () => void,
  // null for a note without checkboxes, else whether all are checked
  checklistDone: () => boolean | null,
  checkAll: (checked: boolean) => void,
  insertDateTime: () => void,
  find: () => void,
};

type Open = {
  top: number,
  right: number,
  focusFirst: boolean,
  editing: boolean,
};

// a note's actions behind one button
export const NoteMenu = (
  {
    note,
    onTogglePin,
    onToggleArchived,
    onWidth,
    onTrash,
    onRestore,
    onDelete,
    editor,
  }: Props,
) => {
  const [open, setOpen] = useState<Open | null>(null);
  const button = useRef<HTMLButtonElement>(null);

  const close = (refocus = false) => {
    setOpen(null);
    if (refocus) button.current?.focus();
  };

  // a click with no pointer behind it came from the keyboard
  const toggle = (e: MouseEvent) => {
    if (open || !button.current) return close();
    const r = button.current.getBoundingClientRect();
    setOpen({
      top: r.bottom + 4,
      right: window.innerWidth - r.right,
      focusFirst: e.detail === 0,
      editing: Boolean(editor) && isMobile(),
    });
  };

  // the note's checkboxes as the menu opens
  const done = open?.editing && editor ? editor.checklistDone() : null;

  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <button
        ref={button}
        className="menu-button"
        title="Note actions"
        aria-label="Note actions"
        aria-haspopup="menu"
        aria-expanded={Boolean(open)}
        onClick={e => {
          e.stopPropagation();
          toggle(e);
        }}>
        <MenuIcon />
      </button>
      {open && (
        <PopupMenu
          at={{ top: open.top, right: open.right }}
          focusFirst={open.focusFirst}
          onClose={close}
          opener={button}>
          {open.editing && editor && (
            <>
              <button role="menuitem" onClick={run(editor.insertTable)}>
                <span className="icon">
                  <Table aria-hidden="true" />
                </span>
                Insert table
              </button>
              <button role="menuitem" onClick={run(editor.insertRecipe)}>
                <span className="icon">
                  <ChefHat aria-hidden="true" />
                </span>
                Insert recipe
              </button>
              <button role="menuitem" onClick={run(editor.insertCalc)}>
                <span className="icon">
                  <Calculator aria-hidden="true" />
                </span>
                Insert calculation
              </button>
              <button role="menuitem" onClick={run(editor.insertChecklist)}>
                <span className="icon">
                  <ListChecks aria-hidden="true" />
                </span>
                Checklist
              </button>
              {done !== null && (
                <button
                  role="menuitem"
                  onClick={run(() => editor.checkAll(!done))}>
                  <span className="icon">
                    {done
                      ? <Square aria-hidden="true" />
                      : <SquareCheckBig aria-hidden="true" />}
                  </span>
                  {done ? 'Uncheck all' : 'Check all'}
                </button>
              )}
              <button role="menuitem" onClick={run(editor.insertDateTime)}>
                <span className="icon">
                  <CalendarClock aria-hidden="true" />
                </span>
                Date & Time
              </button>
              <button role="menuitem" onClick={run(editor.find)}>
                <span className="icon">
                  <Search aria-hidden="true" />
                </span>
                Find in note
              </button>
              <hr />
            </>
          )}
          {note.trashed && (
            <>
              <button role="menuitem" onClick={run(onRestore)}>
                <span className="icon">
                  <RotateCcw aria-hidden="true" />
                </span>
                Restore
              </button>
              <hr />
              <button
                role="menuitem"
                className="danger"
                onClick={run(onDelete)}>
                <span className="icon">
                  <Trash aria-hidden="true" />
                </span>
                Delete forever
              </button>
            </>
          )}
          {!note.trashed && (<>
            <button role="menuitem" onClick={run(onTogglePin)}>
              <span className="icon">
                {note.pinned
                  ? <PinOff aria-hidden="true" />
                  : <Pin aria-hidden="true" />}
              </span>
              {note.pinned ? 'Unpin' : 'Pin'}
            </button>
            <button role="menuitem" onClick={run(onToggleArchived)}>
              <span className="icon">
                {note.archived
                  ? <ArchiveRestore aria-hidden="true" />
                  : <Archive aria-hidden="true" />}
              </span>
              {note.archived ? 'Unarchive' : 'Archive'}
            </button>
            <label className="width">
              <span className="icon">
                <MoveHorizontal aria-hidden="true" />
              </span>
              Width
              <span className="select">
                <select
                  value={note.width}
                  onChange={e => onWidth(Number(e.target.value))}>
                  {[...new Set([...WIDTHS, note.width])]
                    .sort((a, b) => a - b)
                    .map(w => <option key={w} value={w}>{w}%</option>)}
                </select>
                <ChevronDown aria-hidden="true" />
              </span>
            </label>
            <hr />
            <button role="menuitem" className="danger" onClick={run(onTrash)}>
              <span className="icon">
                <Trash aria-hidden="true" />
              </span>
              Delete
            </button>
          </>)}
        </PopupMenu>
      )}
    </>
  );
};

const MenuIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="3" cy="8" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="13" cy="8" r="1.5" />
  </svg>
);
